/**
 * EWBCI メインカスタムフック
 */

import { useEffect, useCallback, useRef, useReducer } from 'react';

import type { EWBCIResponse, ItpcmdRequestInput } from '@/feature/api-client';
import Log from '@/Logger';

import { EWBCIClient } from '../core/client';
import { ConnectionStatus } from '../core/types';
import type { CommonResponseBody, EWBCIClientConfig, BlobURL, PageEventHandlers } from '../core/types';
import { EWBCIError, EWBCIErrorType } from '../utils/errors';

import { useChunkedMessage } from './useChunkedMessage';

export type ItpcmdRequest = ItpcmdRequestInput;
export type ItpcmdResponse = CommonResponseBody<object>;

export interface EWBCIState {
    connectionStatus: ConnectionStatus;
    sessionId: string | null;
}

type Action = {
    type: 'ON_SERVER_CONNECTING';
    sessionId: string | null;
} | {
    type: 'ON_STATUS_CHANGED_WITHOUT_EFFECT';
    status: ConnectionStatus;
} | {
    type: 'ON_DISCONNECTED';
};
/** Reducer定義 */
const reducer = (state: EWBCIState, action: Action) => {
    const logger = Log.getLogger();
    switch (action.type) {
        case 'ON_SERVER_CONNECTING':
            return { ...state, connectionStatus: ConnectionStatus.SERVER_CONNECTING, sessionId: action.sessionId };

        case 'ON_STATUS_CHANGED_WITHOUT_EFFECT':
            return { ...state, connectionStatus: action.status };

        case 'ON_DISCONNECTED':
            return { ...state, connectionStatus: ConnectionStatus.DISCONNECTED, sessionId: null };

        default:
            logger.api.error("useEWBCI.reducer", "UnknownAction", { action });
            return state;
    }
}

/**
 * EWBCI フックの戻り値（型安全版）
 */
export interface EWBCIHooks {
    /** 現在の接続状態 */
    state: EWBCIState;
    /** 
     * itpcmdを実行する
     * 
     * @param itpcmd ItpcmdRequest
     * @param requestId 任意のリクエストID。指定しない場合は自動生成される
     * @returns ItpcmdResponse
     */
    executeItpcmd: (itpcmd: ItpcmdRequest, requestId?: string) => Promise<ItpcmdResponse>,
    /** 
     * itpcmdを実行した結果、作成されるファイルを取得する
     * 
     * @param itpcmd ItpcmdRequest
     * @param requestId 任意のリクエストID。指定しない場合は自動生成される
     * @returns BlobURL
     * 
     * BlobURLとして返すため、不要になったらURL.revokeObjectURLで解放すること
     * 例: 
     * ```
     * const blobUrl = await executeItpcmdWithGettingFile(itpcmd);
     * // 利用例: <a href={blobUrl} download="filename">Download</a>
     * // 不要になったら解放
     * URL.revokeObjectURL(blobUrl);
     * ```
     */
    getFileWithItpcmd: (itpcmd: ItpcmdRequest, requestId?: string) => Promise<BlobURL>,

    /** 文書関連のファイルを取得する */
    // getDocumentFile: (docid: string, filename: string, requestId?: string) => Promise<Blob | undefined>,
    /** 最新の文書PDFを取得する */
    // getLatestDocumentPDF: (docid: string, requestId?: string) => Promise<Blob | undefined>,

    /** 
     * ページの更新検知の開始
     * 
     * @param pageId ページID
     * @param handlers イベントハンドラー
     * @param requestId 任意のリクエストID。指定しない場合は自動生成される
     */
    subscribePage: (pageId: string, handlers: PageEventHandlers, requestId?: string) => Promise<void>,

    /** 
     * ページの更新検知の停止
     */
    unsubscribePage: () => Promise<void>,

    /** クライアントの参照（内部向け） */
    _client: React.RefObject<EWBCIClient | null>;
}

/**
 * EWBCI API Client を使用するためのメインカスタムフック
 * 
 * EWBCIの状態と基本的な操作を提供する。
 * - itpcmdの実行（Request/Response型）
 * - ファイル取得
 * - ページの更新検知の開始/終了
 * 
 * @param config クライアント設定
 * @returns EWBCI操作用の関数とステート
 */
export function useEWBCI(config: EWBCIClientConfig): EWBCIHooks {
    const { tenantId, serialId, deviceId } = config    // ステート
    const [state, dispatch] = useReducer(reducer, { connectionStatus: ConnectionStatus.NONE, sessionId: null });

    // クライアントインスタンス
    const clientRef = useRef<EWBCIClient | null>(null);
    // チャンクメッセージ完成時コールバック
    const onCompleteChunkedMessageCallback = useCallback((completeMessage: EWBCIResponse) => {
        if (clientRef.current) {
            clientRef.current.handleMessage(completeMessage);
        }
    }, []);
    // チャンクメッセージ処理フック
    const { processChunk, clearAllChunks } = useChunkedMessage({
        // chunkTimeout: 5 * 60 * 1000, // 5分
        onComplete: onCompleteChunkedMessageCallback,
    });

    // クライアントの初期化
    useEffect(() => {
        const logger = Log.getLogger();
        logger.ui.info('useEWBCI', 'initialize', `Initializing EWBCI client with serialId: ${serialId}, deviceId: ${deviceId}`);

        const client = new EWBCIClient({ tenantId, serialId, deviceId }, { processChunk, clearAllChunks });

        // イベントハンドラーを設定
        client.setOnChangeConnectionStatus((status: ConnectionStatus) => {
            // clientが通知してくるConnectionStatusをフックが返す状態として伝達する
            logger.ui.debug('useEWBCI', 'onStatusChange', `Status changed to: ${status}`);
            switch (status) {
                case ConnectionStatus.SERVER_CONNECTING:
                    // SERVER_CONNECTINGへの遷移時に新しいsessionIdが確定する
                    dispatch({ type: 'ON_SERVER_CONNECTING', sessionId: client.getSessionId() });
                    break;
                case ConnectionStatus.SERVER_DISCONNECTED:
                    // SERVER_DISCONNECTEDへの遷移時にsessionIdが無効になる
                    dispatch({ type: 'ON_DISCONNECTED' });
                    break;
                default:
                    // その他の副作用なしで状態を更新
                    dispatch({ type: 'ON_STATUS_CHANGED_WITHOUT_EFFECT', status });
                    break;
            }
        });

        clientRef.current = client;

        // 非同期で接続開始
        (async () => client.connect())(); // throw no error here, errors are handled in the client internally

        // クリーンアップ
        return () => {
            logger.ui.info('useEWBCI', 'cleanup', 'Cleaning up EWBCI client');
            client.disconnect(); // 切断
            if (clientRef.current === client) {
                clientRef.current = null;
            }
        };
    }, [processChunk, clearAllChunks, tenantId, serialId, deviceId]);

    /**
     * itpcmdを実行する
     */
    const executeItpcmd = useCallback(async (itpcmd: ItpcmdRequestInput, requestId?: string): Promise<CommonResponseBody<object>> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            const error = new EWBCIError('Client not initialized', EWBCIErrorType.AssertError);
            logger.ui.error('useEWBCI', 'sendRequest', error.message);
            throw error;
        }
        try {
            logger.ui.debug('useEWBCI', 'executeItpcmd', `Sending request...`);
            const result = await clientRef.current.executeItpcmd(itpcmd, requestId);
            logger.ui.debug('useEWBCI', 'executeItpcmd', 'Received response.');

            return result.body;

        } catch (error) {
            const ewbciError = new EWBCIError(`Failed to executeItpcmd${requestId ? `(${requestId})` : ''}`, EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCI', 'executeItpcmd', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, []);

    /**
     * itpcmdを実行した結果、作成されるファイルを取得する
     */
    const getFileWithItpcmd = useCallback(async (itpcmd: ItpcmdRequestInput, requestId?: string): Promise<BlobURL> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            const error = new EWBCIError('Client not initialized', EWBCIErrorType.AssertError);
            logger.ui.error('useEWBCI', 'getFileWithItpcmd', error.getDetailedMessage());
            throw error;
        }

        try {
            logger.ui.debug('useEWBCI', 'getFileWithItpcmd', `Sending request...`);
            const result = await clientRef.current.getFileWithItpcmd(itpcmd, requestId);
            logger.ui.debug('useEWBCI', 'getFileWithItpcmd', 'Received response.');

            if (!result) {
                const error = new EWBCIError('No file returned from itpcmd execution', EWBCIErrorType.AssertError);
                logger.ui.error('useEWBCI', 'getFileWithItpcmd', error.getDetailedMessage());
                throw error;
            }

            const url = URL.createObjectURL(result);
            // クリーンアップ用に記録

            return url;

        } catch (error) {
            const ewbciError = new EWBCIError(`Failed to getFileWithItpcmd${requestId ? `(${requestId})` : ''}`, EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCI', 'getFileWithItpcmd', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, []);

    /**
     * ページの更新検知の開始
     */
    const subscribePage = useCallback(async (pageId: string, handlers: PageEventHandlers): Promise<void> => {
        const logger = Log.getLogger();
        if (!clientRef.current) {
            const error = new EWBCIError('Client not initialized', EWBCIErrorType.AssertError);
            logger.ui.error('useEWBCI', 'subscribePage', error.getDetailedMessage());
            throw error;
        }

        try {
            logger.ui.info('useEWBCI', 'subscribePage', `Starting page subscription for pageId: ${pageId}`);

            // ページサブスクライブを開始(接続が完了していなければ予約される)
            await clientRef.current.subscribePage(pageId, handlers, false); // reconnect: false

            logger.ui.info('useEWBCI', 'subscribePage', 'Page subscription started successfully');

        } catch (error) {
            const ewbciError = new EWBCIError(`Failed to subscribePage`, EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCI', 'subscribePage', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, []);

    /**
     * ページの更新検知の停止
     */
    const unsubscribePage = useCallback(async (): Promise<void> => {
        const logger = Log.getLogger();
        if (!clientRef.current) return; // クライアントがないなら何もしない

        try {
            logger.ui.info('useEWBCI', 'unsubscribePage', 'Stopping page subscription');

            await clientRef.current.unsubscribePage();

            logger.ui.info('useEWBCI', 'unsubscribePage', 'Page subscription stopped successfully');

        } catch (error) {
            const ewbciError = new EWBCIError(`Failed to unsubscribePage`, EWBCIErrorType.Unknown, error);
            logger.ui.error('useEWBCI', 'unsubscribePage', ewbciError.getDetailedMessage());
            throw ewbciError;
        }
    }, []);

    return {
        state,
        executeItpcmd,
        getFileWithItpcmd,
        subscribePage,
        unsubscribePage,
        _client: clientRef,
    };
}

/* istanbul ignore next: MT用export指示のためカバレッジを取得しない */
export const __test__ = import.meta.env.NODE_ENV !== 'test' ? {} : { reducer };
